import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import { CustomerActivityService } from '@customer-activity/customer-activity.service';
import { FoldersStoreReadService } from '@folders/store/services/folders-store-read.service';
import * as savedSearchActions from '../actions/saved-search.actions';
import { SavedSearchStoreService } from '../services/saved-search-store.service';
import { SimpleDialogService } from '@core-utils/simple-dialog/services/simple-dialog.service';
import { AddEditSavedSearchDialogComponent } from '@saved-search/components/add-edit-saved-search-dialog/add-edit-saved-search-dialog.component';
import { GoogleAnalyticsEventName } from 'app/modules/core-modules/enums/google-analytics-event-name';
import * as googleAnalyticsActions from '@core-layout/app/store/actions/google-analytics.actions';
import { SavedSearchListComponent } from '@saved-search/components/saved-search-list/saved-search-list.component';
import { SaveYourSearchComponent } from '@saved-search/components/save-your-search/save-your-search.component';
import { MatchMediaService } from '@media/services/match-media.service';
import { SavedSearchData } from '@saved-search/models/saved-search-data';
import { RpcDialogService } from '@core-services/dialog/rpc-dialog.service';
import { SearchStoreService } from '@search/store/services/search-store.service';
import { ListingCategories } from '@listings/enums/listing-categories.enum';

@Injectable()
export class SavedSearchDialogsEffects {
    constructor(
        private readonly actions$: Actions,
        public readonly customerActivityService: CustomerActivityService,
        private readonly foldersStoreReadService: FoldersStoreReadService,
        private readonly savedSearchStoreService: SavedSearchStoreService,
        private readonly simpleDialogService: SimpleDialogService,
        private readonly rpcDialogService: RpcDialogService,
        private readonly matchMediaService: MatchMediaService,
        private readonly searchStoreService: SearchStoreService,
    ) { }

    public readonly showSavedSearchList$ = createEffect(
        () => this.actions$.pipe(
            ofType(savedSearchActions.showSavedSearchList),
            switchMap(() => {
                const dialogRef = this.matchMediaService.activeMediaQuery === 'xs'
                    ? this.rpcDialogService.openBottomSheet(SavedSearchListComponent, null, { customClass: ['saved-search-bottomsheet', 'bottom-panel-wrap'] })
                    : this.rpcDialogService.openDialog(SavedSearchListComponent, null, { customClass: ['saved-search-modal'] });

                return dialogRef.componentInstance.closed.pipe(tap(() => dialogRef.close()));
            })
        ),
        { dispatch: false }
    );

    public readonly showSaveYourSearch$ = createEffect(() => this.actions$.pipe(
        ofType(savedSearchActions.showSaveYourSearch),
        concatLatestFrom(() => this.savedSearchStoreService.savedSearchesToModify$),
        map(([, savedSearches]) => {
            return savedSearches.length === 0
                ? savedSearchActions.showAddEditSavedSearchDialog({ savedSearchId: null })
                : savedSearchActions.showSaveYourSearchDialog();
        })
    ));

    public readonly showSaveYourSearchDialog$ = createEffect(
        () => this.actions$.pipe(
            ofType(savedSearchActions.showSaveYourSearchDialog),
            switchMap(() => {
                const dialogRef = this.matchMediaService.activeMediaQuery === 'xs'
                    ? this.rpcDialogService.openBottomSheet(SaveYourSearchComponent, null, { customClass: ['save-your-search-bottomsheet', 'bottom-panel-wrap'] })
                    : this.rpcDialogService.openDialog(SaveYourSearchComponent, null, { customClass: 'save-your-search-modal' });

                return dialogRef.componentInstance.closed.pipe(tap(() => dialogRef.close()));
            })
        ),
        { dispatch: false }
    );

    public readonly showDeleteSavedSearchDialog$ = createEffect(() => this.actions$.pipe(
        ofType(savedSearchActions.showDeleteSavedSearchDialog),
        switchMap(({ savedSearch, actionOnSuccess }) => {
            const config = {
                showTitle: true,
                showCancel: true,
                title: 'DIALOGS.DELETE_SAVED_SEARCH',
                message: 'DIALOGS.SAVED_SEARCH_DELETION',
                confirmButtonText: 'DIALOGS.BUTTONS.DELETE',
                customClass: 'remove-save-your-search-modal'
            };

            return this.simpleDialogService.showForStream(config).pipe(
                filter(Boolean),
                tap(() => actionOnSuccess()),
                map(() => savedSearchActions.deleteSavedSearch({ savedSearch }))
            );
        }),
    ));

    public readonly showAddEditSavedSearchDialog$ = createEffect(() => this.actions$.pipe(
        ofType(savedSearchActions.showAddEditSavedSearchDialog),
        concatLatestFrom(({ savedSearchId }) => [
            this.savedSearchStoreService.savedSearches$,
            this.savedSearchStoreService.getSavedSearch(savedSearchId),
            this.foldersStoreReadService.foldersSavedSearches$,
            this.searchStoreService.isRentalsSearchOption$,
        ]),
        switchMap(([{ savedSearchId }, savedSearches, savedSearch, foldersSavedSearches, isRentalsSearchOption]) => {
            const folderId = foldersSavedSearches.find(x => x.savedSearchId === savedSearchId)?.folderId;
            const data = {
                savedSearches,
                savedSearch,
                folderId,
                isRentalsSearchOption: savedSearch != null
                    ? savedSearch.criteria.categoryId === ListingCategories.Rental
                    : isRentalsSearchOption
            };

            return this.rpcDialogService.openDialog(AddEditSavedSearchDialogComponent, data, { customClass: 'saved-search-edit-modal' }).closed.pipe(
                filter(Boolean),
                switchMap((result: SavedSearchData) => {
                    const isUpdate = savedSearch != null;
                    const savedSearchModification = { ...result, updateCriteria: !isUpdate };

                    const actions: Action[] = [savedSearch != null
                        ? savedSearchActions.updateSavedSearchRequested({ savedSearchModification })
                        : savedSearchActions.createSavedSearchRequested({ savedSearchModification })
                    ];

                    if (isUpdate) {
                        if (folderId !== result.folderId) {
                            actions.push(googleAnalyticsActions.addEvent({ name: GoogleAnalyticsEventName.SavedSearchEdition_AttachFolder }));
                        }
                    } else {
                        actions.push(googleAnalyticsActions.addEvent({ name: GoogleAnalyticsEventName.SavedSearchCreation }));

                        if (result.folderId != null) {
                            actions.push(googleAnalyticsActions.addEvent({ name: GoogleAnalyticsEventName.SavedSearchCreation_AttachFolder }));
                        }
                    }

                    return actions;
                })
            );
        }),
    ));
}